import { z } from 'zod'

const promptVariableSchema = z.object({
  name: z.string().min(1).max(50),
  label: z.string().min(1).max(100),
  type: z.enum(['TEXT', 'NUMBER', 'SELECT', 'TEXTAREA']),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean(),
  defaultValue: z.string().optional(),
})

export const useCaseIndustrySchema = z.enum([
  'SAAS',
  'ECOMMERCE',
  'AGENCY',
  'FREELANCE',
  'STARTUP',
  'ENTERPRISE',
  'EDUCATION',
  'LEGAL',
  'PUBLIC_ADMINISTRATION',
  'REAL_ESTATE',
  'CONSTRUCTION',
  'HEALTHCARE',
  'FINANCE',
  'HR',
  'SALES',
  'CUSTOMER_SUPPORT',
  'OPERATIONS',
  'PERSONAL_LIFE',
  'GENERIC',
])

export const promptUseCaseSchema = z.object({
  id: z.string().optional(), // généré côté API si absent
  title: z.string().min(3).max(120),
  scenario: z.string().min(10).max(400),
  industry: useCaseIndustrySchema.default('GENERIC'),
  tags: z.array(z.string().max(30)).max(8).default([]),
  exampleInput: z.string().max(600).optional(),
  order: z.number().int().min(0).default(0),
})

export function parsePromptUseCases(input: unknown) {
  const result = z.array(promptUseCaseSchema).safeParse(input)
  return result.success ? result.data : []
}

/**
 * Parse and validate the variables JSON of a Prompt.
 * Returns [] when the payload is invalid (don't crash the API on malformed legacy data).
 */
export function parsePromptVariables(input: unknown) {
  const result = z.array(promptVariableSchema).safeParse(input)
  return result.success ? result.data : []
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Migrate a legacy variable shape (`{ key, description, example }`) to the canonical
 * shape used by the front (`{ name, label, type, placeholder, defaultValue, required }`).
 * Idempotent: if already canonical, returns as-is.
 */
export function migrateLegacyVariable(v: unknown): unknown {
  if (!v || typeof v !== 'object') return v
  const obj = v as Record<string, unknown>
  if (typeof obj.name === 'string' && typeof obj.label === 'string') return obj
  if (typeof obj.key === 'string') {
    const example = typeof obj.example === 'string' ? obj.example : undefined
    return {
      name: obj.key,
      label: typeof obj.description === 'string' ? obj.description : obj.key,
      type: 'TEXT' as const,
      placeholder: example,
      defaultValue: example,
      required: false,
    }
  }
  return obj
}

/**
 * Wrap variable placeholders in a prompt's content with double-curly braces.
 * E.g. given variables `[{ name: 'ROLE' }]`, `"Tu es ROLE"` becomes `"Tu es ROLE"`.
 * - Idempotent: tokens already wrapped are not re-wrapped.
 * - Uses word boundaries to avoid partial matches (e.g. ROLE inside ROLES).
 * - Sorts by name length desc to avoid clobbering longer names with shorter ones.
 */
export function wrapPromptPlaceholders(
  content: string,
  variables: ReadonlyArray<{ name?: string; key?: string }>,
): string {
  if (!content || !variables?.length) return content
  const names = variables
    .map((v) => v.name ?? v.key)
    .filter((n): n is string => typeof n === 'string' && n.length > 0)
    .sort((a, b) => b.length - a.length)
  let out = content
  for (const name of names) {
    const re = new RegExp(`(?<!\\{\\{)\\b${escapeRegex(name)}\\b(?!\\}\\})`, 'g')
    out = out.replace(re, `{{${name}}}`)
  }
  return out
}

export const createPromptSchema = z.object({
  title: z.string().min(3, 'Min 3 caractères').max(150),
  description: z.string().min(10, 'Min 10 caractères').max(500),
  content: z.string().min(10, 'Min 10 caractères'),
  category: z.enum([
    'WRITING',
    'CODING',
    'MARKETING',
    'PRODUCTIVITY',
    'ANALYSIS',
    'CREATIVE',
    'BUSINESS',
    'EDUCATION',
    'CUSTOM',
  ]),
  aiModel: z.enum(['GPT4', 'CLAUDE', 'GEMINI', 'MISTRAL', 'UNIVERSAL']).default('UNIVERSAL'),
  tags: z.array(z.string().max(30)).max(10).optional(),
  variables: z.array(promptVariableSchema).max(20).optional(),
  useCases: z.array(promptUseCaseSchema).max(6).optional(),
  expectedOutput: z.string().max(1000).optional(),
  order: z.number().int().min(0).optional(),
})

export const searchPromptsSchema = z.object({
  query: z.string().max(200).optional(),
  categories: z
    .array(
      z.enum([
        'WRITING',
        'CODING',
        'MARKETING',
        'PRODUCTIVITY',
        'ANALYSIS',
        'CREATIVE',
        'BUSINESS',
        'EDUCATION',
        'ADMIN_LEGAL',
        'REAL_ESTATE',
        'CONSTRUCTION',
        'EXECUTIVE_ASSISTANT',
        'FINANCE_ACCOUNTING',
        'HR_CAREER',
        'SALES',
        'CUSTOMER_SUPPORT',
        'OPERATIONS',
        'PERSONAL_LIFE',
        'CUSTOM',
      ]),
    )
    .optional(),
  aiModels: z.array(z.enum(['GPT4', 'CLAUDE', 'GEMINI', 'MISTRAL', 'UNIVERSAL'])).optional(),
  tags: z.array(z.string()).max(10).optional(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
  sortBy: z.enum(['relevance', 'copyCount', 'createdAt', 'title']).default('relevance'),
})

export type CreatePromptInput = z.infer<typeof createPromptSchema>
export type SearchPromptsInput = z.infer<typeof searchPromptsSchema>
